import React, { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { GET_ORDER_BY_ID } from '../OrderQueries'
import { PageContainer } from '@/components/shared/page-container'
import { HeaderSection } from '@/components/shared/header-section'
import { TableCard } from '@/components/shared/table-card'
import { OrderSummarySection } from '@/containers/Orders/components/OrderSummarySection'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Order } from '@/types/order'
import { OrderStatusBadge } from '@/components/shared/OrderStatusBadge'
import { AlertCircle, MoreHorizontal } from 'lucide-react'
import { OrderMetadata } from './OrderMetadata'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { CartItemsTable } from './CartItemsTable'

// Loading skeleton component for better code organization
const LoadingSkeleton = () => (
  <PageContainer>
    <Card className="p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
    </Card>
  </PageContainer>
)

// Error component for consistent error display
const ErrorDisplay = ({ message }: { message: string }) => (
  <PageContainer>
    <Card className="p-6">
      <div className="flex items-center text-red-500 gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Error loading order: {message}</span>
      </div>
    </Card>
  </PageContainer>
)

export default function OrderDetails() {
  const { orderId } = useParams()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery<{ findUniqueOrder: Order }>(
    GET_ORDER_BY_ID,
    {
      variables: { id: orderId },
      // Add error policy to handle partial data
      errorPolicy: 'all',
      // Add fetch policy to optimize caching
      fetchPolicy: 'cache-first',
    }
  )

  // Memoize the order data to prevent unnecessary re-renders
  const order = useMemo(() => data?.findUniqueOrder, [data])

  if (loading) return <LoadingSkeleton />
  if (error) return <ErrorDisplay message={error.message} />
  if (!order) {
    return (
      <PageContainer>
        <Card className="p-6">
          <div className="flex items-center text-gray-500 gap-2">
            <AlertCircle className="h-5 w-5" />
            <span>Order not found</span>
          </div>
        </Card>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <HeaderSection
        title={`Order ${order.order_code}`}
        subtitle={`Created on ${new Date(
          order.createdAt
        ).toLocaleDateString()}`}
      >
        <OrderStatusBadge status={order.order_status} />
      </HeaderSection>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-2 space-y-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              Products
            </CardHeader>
            <CardContent>
              <CartItemsTable
                orderItems={order.order_items}
                totalPriceConfirmed={order.total_price_confirmed || undefined}
              />
            </CardContent>
          </Card>
          <OrderMetadata metadata={order.metadata} />
        </div>

        <OrderSummarySection order={order} />
      </div>
    </PageContainer>
  )
}
